import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import './AndroidPackagePanel.css';

const ROLE_NAMES = { customer: 'לקוח', client: 'לקוח', staff: 'צוות', courier: 'שליח', driver: 'נהג', manager: 'מנהל', employee: 'עובד', member: 'חבר', dispatcher: 'סדרן', operator: 'מפעיל', agent: 'נציג', teacher: 'מורה', student: 'תלמיד', patient: 'מטופל', doctor: 'רופא' };
export function roleLabel(role) { return ROLE_NAMES[String(role || '').toLowerCase()] || role; }

/** The Preview link with a fragment added (keeps an existing fragment). */
export function withFragment(link, fragment) {
  if (!link || !fragment) return link;
  const [base, hash = ''] = String(link).split('#');
  return `${base}#${hash ? `${hash}&` : ''}${fragment}`;
}

/**
 * Getting started with a managed Full-Stack app: how the owner enters (already as owner from
 * CodeVoyage), "view as <role>" links for the Preview, the app's admin panel, demo data for the
 * Preview only, and the operational check result. Nothing here touches Production.
 */
export default function AppStartPanel({ projectId, previewLink }) {
  const [info, setInfo] = useState(null);
  const [roles, setRoles] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setInfo(await api.appStart(projectId)); }
    catch (err) { setError(err.response?.data?.error || 'לא הצלחנו לטעון את פרטי המערכת.'); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key); setError(''); setMessage('');
    try { await fn(); } catch (err) { setError(err.response?.data?.error || 'הפעולה נכשלה. נסו שוב.'); } finally { setBusy(''); }
  };
  const openRoles = () => run('roles', async () => setRoles((await api.previewRoles(projectId)).roles || []));
  const demo = () => run('demo', async () => {
    const result = await api.loadDemoData(projectId);
    const created = Object.entries(result.created || {}).filter(([, count]) => count > 0).map(([name, count]) => `${name}: ${count}`);
    setMessage(created.length ? `נוספו נתוני הדגמה ל־Preview בלבד (${created.join(', ')}). רעננו את התצוגה.` : 'לא נוספו נתונים - כבר יש נתונים במערכת.');
  });
  const check = () => run('check', async () => {
    const result = await api.runOperationalCheck(projectId);
    setInfo(current => ({ ...(current || {}), operational: result.operational || null }));
  });

  if (error && !info) return <div className="chat-next-steps-panel" dir="rtl"><p>{error}</p></div>;
  if (!info) return <div className="chat-next-steps-panel" dir="rtl"><p>טוען…</p></div>;
  if (!info.managed) return <div className="chat-next-steps-panel" dir="rtl"><p>לפרויקט הזה אין מסד נתונים מנוהל.</p></div>;
  const appRoles = info.auth?.roles || [];
  return (
    <div className="chat-next-steps-panel" dir="rtl">
      <p><strong>אתם נכנסים כבעלים.</strong> קישור ה־Preview מ־CodeVoyage פותח לכם את המערכת עם הרשאות מלאות, בלי סיסמה.</p>
      {info.operational && (
        <p className={info.operational.status === 'issues' ? 'android-package-error' : ''} style={{ whiteSpace: 'pre-line' }}>{info.operational.summary}</p>
      )}
      <div className="chat-next-steps-actions">
        {appRoles.length > 0 && <button type="button" className="btn" disabled={Boolean(busy)} onClick={openRoles}>{busy === 'roles' ? 'מכין…' : '👀 צפייה בתור תפקיד'}</button>}
        {info.adminUrl && <a className="btn" href={info.adminUrl} target="_blank" rel="noreferrer">🛠️ פאנל ניהול ומשתמשים</a>}
        <button type="button" className="btn" disabled={Boolean(busy)} onClick={demo}>{busy === 'demo' ? 'מוסיף…' : '🧪 נתוני הדגמה (Preview)'}</button>
        <button type="button" className="btn" disabled={Boolean(busy)} onClick={check}>{busy === 'check' ? 'בודק…' : '✅ בדיקת תפקוד'}</button>
      </div>
      {roles && (
        <div>
          <p>כל קישור פותח את ה־Preview כמשתמש בדיקה עם התפקיד הזה בלבד (בנתוני ה־Preview, לא ב־Production):</p>
          <ul>
            {roles.map(item => <li key={item.role}><a href={withFragment(previewLink, item.fragment)} target="_blank" rel="noreferrer">כניסה בתור {roleLabel(item.role)}</a></li>)}
          </ul>
        </div>
      )}
      {message && <p>{message}</p>}
      {error && <p className="android-package-error">{error}</p>}
      <details>
        <summary>מה יש במערכת</summary>
        {appRoles.length > 0 && <p>תפקידים: {appRoles.map(roleLabel).join(', ')}. משתמשים חדשים נרשמים בעצמם{info.auth?.signup === 'open' ? '' : ' (בהזמנה)'}; משתמשים קבועים ותפקידים מנהלים בפאנל הניהול.</p>}
        {info.entities?.length > 0 && <p>נתונים: {info.entities.map(item => item.title).join(', ')}.</p>}
        {info.actions?.length > 0 && <p>פעולות: {info.actions.map(item => item.label).join(', ')}.</p>}
      </details>
    </div>
  );
}
